import { Input, MMButton, ModalTitle, SelectAccount } from '@components'
import { FISCAL_QUARTER } from '@constants'
import cn from '@integrations/classnames'
import { useContext, useEffect, useState } from '@integrations/react'
import { useAppSelector } from '@store'
import styles from '@transactions/scss/balanceStatementModal.module.scss'
import { normalizeLanguageCode } from '@utils/language'
import {
  StatementPeriodTypes,
  selectCurrentLanguage,
  useDownloadTransactionStatementCsv,
  useMerchantQry
} from 'mmfintech-backend-api'
import type { StatementPeriod, StatementPeriodType } from 'mmfintech-backend-api/types'
import {
  compareDates,
  currentDay,
  currentMonth,
  currentYear,
  firstDateOfMonth,
  fixDateOnly,
  GlobalContext,
  lastDayOfMonth,
  monthOptions,
  prepareDate,
  tr,
  useFormValues
} from 'mmfintech-commons'
import { ErrorDisplay, MMRadioGroup } from 'mmfintech-portal-commons'

function TransactionsExportModal({ account, accounts, dateFrom, dateTo }) {
  const { modalHide } = useContext(GlobalContext)
  const selectedLanguage = normalizeLanguageCode(useAppSelector(selectCurrentLanguage))

  const [error, setError] = useState(null)
  const [periodType, setPeriodType] = useState<StatementPeriodType>(StatementPeriodTypes.CUSTOM)
  const [selectedAccount, setSelectedAccount] = useState(account)
  const [openPicker, setOpenPicker] = useState<'startDate' | 'endDate' | null>(null)

  const { merchant } = useMerchantQry()

  const formValues = useFormValues({
    quarter: { value: FISCAL_QUARTER.Q1 },
    month: { value: currentMonth() + 1 },
    year: { value: currentYear() },
    startDate: { value: dateFrom ? dateFrom : firstDateOfMonth() },
    endDate: { value: dateTo ? dateTo : new Date() }
  })

  const { downloadStatementCsv, downloadStatementCsvError, downloadStatementCsvFetching } =
    useDownloadTransactionStatementCsv()

  //const fiscalMonths = Object.values(FISCAL_MONTH)
  const fiscalQuarters = Object.values(FISCAL_QUARTER)

  // Clicks inside the modal never reach document (ModalDialog stops mousedown), so react-datepicker
  // can't auto-close one calendar when the other opens; share the open state instead.
  function pickerProps(name: 'startDate' | 'endDate') {
    return {
      open: openPicker === name,
      onInputClick: () => setOpenPicker(name),
      onCalendarOpen: () => setOpenPicker(name),
      onCalendarClose: () => setOpenPicker((current) => (current === name ? null : current))
    }
  }

  function getYears() {
    const { createdOn } = merchant || {}
    const year2 = prepareDate(createdOn).getFullYear()
    const res = []
    for (let y = currentYear(); y >= year2; y--) {
      res.push(y)
    }
    return res
  }

  async function handleSubmit(e) {
    e.preventDefault()

    if (!selectedAccount) {
      setError(tr('FRONTEND.STATEMENT.EXPORT.ERROR_ACCOUNT', 'Account is not selected.'))
      return
    }

    let period: StatementPeriod

    switch (periodType) {
      case StatementPeriodTypes.MONTHLY: {
        const startDate = new Date(formValues.getValue('year'), formValues.getValue('month') - 1, 1)
        if (compareDates(startDate, new Date()) > 0) {
          setError(tr('FRONTEND.STATEMENT.EXPORT.ERROR_PERIOD', 'The period should be before current date.'))
          return
        }

        const endDate = lastDayOfMonth(startDate)
        if (endDate.getFullYear() === currentYear() && endDate.getMonth() === currentMonth()) {
          endDate.setDate(currentDay())
        }
        if (compareDates(endDate, new Date()) > 0) {
          setError(tr('FRONTEND.STATEMENT.EXPORT.ERROR_PERIOD', 'The period should be before current date.'))
          return
        }

        period = {
          periodType: StatementPeriodTypes.CUSTOM,
          startDate: fixDateOnly(startDate),
          endDate: fixDateOnly(endDate)
        }
        // period = {
        //   periodType,
        //   month: formValues.getValue('month'), // [ M1, M2, M3, M4, M5, M6, M7, M8, M9, M10, M11, M12 ]
        //   year: formValues.getValue('year')
        // }
        break
      }

      case StatementPeriodTypes.QUARTERLY:
        period = {
          periodType,
          quarter: formValues.getValue('quarter'), // [ Q1, Q2, Q3, Q4 ]
          year: formValues.getValue('year')
        }
        break

      case StatementPeriodTypes.YEARLY:
        period = {
          periodType,
          year: formValues.getValue('year')
        }
        break

      default: {
        const startDateValue = formValues.getValue('startDate')
        const endDateValue = formValues.getValue('endDate')

        if (!startDateValue || !endDateValue) {
          setError(tr('FRONTEND.STATEMENT.EXPORT.ERROR_PERIOD_REQUIRED', 'Please select both From and To dates.'))
          return
        }

        if (compareDates(startDateValue, new Date()) > 0 || compareDates(endDateValue, new Date()) > 0) {
          setError(tr('FRONTEND.STATEMENT.EXPORT.ERROR_PERIOD', 'The period should be before current date.'))
          return
        }

        period = {
          periodType: StatementPeriodTypes.CUSTOM,
          startDate: fixDateOnly(startDateValue),
          endDate: fixDateOnly(endDateValue)
        }
      }
    }

    const data = {
      accountId: selectedAccount.id,
      period: period
    }

    downloadStatementCsv(data, modalHide)
  }

  useEffect(() => {
    formValues.handleErrors(downloadStatementCsvError, {
      period: 'startDate',
      'period.startDate': 'startDate',
      'period.endDate': 'endDate'
    })
  }, [downloadStatementCsvError])

  return (
    <div className={cn(styles['statement-wrapper'], 'statement-wrapper')} data-test='transactions-export-period-modal'>
      <form className={cn(styles['statement-form'], 'statement-form')} noValidate onSubmit={handleSubmit}>
        <ModalTitle className={cn(styles['title'], 'title')}>
          {tr('FRONTEND.STATEMENT.EXPORT.TITLE_CSV', 'Transactions CSV Period')}
        </ModalTitle>

        <SelectAccount
          accounts={accounts}
          label={tr('FRONTEND.STATEMENT.EXPORT.ACCOUNT_LABEL', 'Account')}
          selectedAccount={selectedAccount}
          setSelectedAccount={setSelectedAccount}
        />

        <div className={cn('mt-3 mb-3')}>
          <MMRadioGroup
            options={[
              {
                value: StatementPeriodTypes.MONTHLY,
                label: tr('FRONTEND.STATEMENT.EXPORT.PERIOD_MONTHLY', 'Monthly'),
                'data-test': 'MONTHLY'
              },
              // {
              //   value: StatementPeriodTypes.QUARTERLY,
              //   label: tr('FRONTEND.STATEMENT.EXPORT.PERIOD_QUARTERLY', 'Quarterly'),
              //   'data-test': 'QUARTERLY'
              // },
              // {
              //   value: StatementPeriodTypes.YEARLY,
              //   label: tr('FRONTEND.STATEMENT.EXPORT.PERIOD_YEARLY', 'Yearly'),
              //   'data-test': 'YEARLY'
              // },
              {
                value: StatementPeriodTypes.CUSTOM,
                label: tr('FRONTEND.STATEMENT.EXPORT.PERIOD_CUSTOM', 'Custom'),
                'data-test': 'CUSTOM'
              }
            ]}
            selected={periodType}
            setSelected={(val: string) => setPeriodType(val as StatementPeriodType)}
          />
        </div>

        {periodType === StatementPeriodTypes.CUSTOM ? (
          <div className={cn(styles['period-wrap'], 'period-wrap')}>
            <Input
              type='date'
              name='startDate'
              selectedLanguage={selectedLanguage}
              maxDate={new Date()}
              label={tr('FRONTEND.STATEMENT.EXPORT.DATE_FROM_LABEL', 'From')}
              {...formValues.registerInput('startDate')}
              {...pickerProps('startDate')}
              data-test='date-from'
              required
              hideErrorLine
              overlayId='datepicker-portal'
              placement='bottom'
              keepInViewport
            />
            <Input
              type='date'
              name='endDate'
              selectedLanguage={selectedLanguage}
              maxDate={new Date()}
              label={tr('FRONTEND.STATEMENT.EXPORT.DATE_TO_LABEL', 'To')}
              {...formValues.registerInput('endDate')}
              {...pickerProps('endDate')}
              data-test='date-to'
              required
              hideErrorLine
              overlayId='datepicker-portal'
              placement='bottom'
              keepInViewport
            />
          </div>
        ) : (
          <div className={cn(styles['year-month'], 'year-month')}>
            <Input
              type='select'
              data-test='year'
              label={tr('FRONTEND.STATEMENT.EXPORT.YEAR_LABEL', 'Year')}
              options={getYears().map((year) => ({ label: year, value: year }))}
              {...formValues.registerInput('year')}
              menuPosition='fixed'
              menuPortalTarget={document.getElementById('modal-container')}
              required
            />

            {periodType === StatementPeriodTypes.MONTHLY && (
              <Input
                type='select'
                data-test='month'
                label={tr('FRONTEND.STATEMENT.EXPORT.MONTH_LABEL', 'Month')}
                options={monthOptions()}
                {...formValues.registerInput('month')}
                menuPosition='fixed'
                menuPortalTarget={document.getElementById('modal-container')}
                required
              />
            )}

            {periodType === StatementPeriodTypes.QUARTERLY && (
              <Input
                type='select'
                data-test='quarter'
                label={tr('FRONTEND.STATEMENT.EXPORT.QUARTER_LABEL', 'Fiscal quarter')}
                options={fiscalQuarters.map((q) => ({ label: q, value: q }))}
                {...formValues.registerInput('quarter')}
                menuPosition='fixed'
                menuPortalTarget={document.getElementById('modal-container')}
              />
            )}
          </div>
        )}

        <ErrorDisplay error={[downloadStatementCsvError, error]} />

        <MMButton
          type='submit'
          variant='primary'
          caption={tr('FRONTEND.STATEMENT.EXPORT.EXPORT_BUTTON', 'Export transactions')}
          loading={downloadStatementCsvFetching}
          fullWidth
          data-test='button-generate'
        />
      </form>
    </div>
  )
}

export default TransactionsExportModal
